import {MutableRefObject, useEffect, useRef, useState} from "react";

import {useQuery} from "@tanstack/react-query";

import {eatClubClient} from "@globals/config/apiClient";
import {DateString} from "@globals/types/utilityTypes";

export type CutoffTimes = {
  cutoff_days: number;
  order_cutoff: string;
  order_cutoff_time_remaining: number;
  order_cutoff_day_and_time: string;
  delivery_weekday: string;
  delivery_day_of_month: number;
  large_group_order_cutoff: {
    time: string;
    time_remaining: number;
    volume: number;
  };
};

type CutoffTimesProps = {
  selectedDate?: DateString;
  selectedLocation?: number;
  initialCutoffTimes?: CutoffTimes;
};

export type CutoffTimesResource = {
  cutoffTimes?: CutoffTimes;
  isPastCutoffTime: boolean;
  isPastLargeOrderCutoffTime: boolean;
  isFetched: boolean;
  hasPastCutoffTimer: boolean;
  hasPastLargeOrderCutoffTimer: boolean;
};

export const CUTOFF_TIMES_CACHE_KEY = "cutoffTimes";

export function getCutoffTimes(
  menuDate: DateString,
  locationId: number
): Promise<CutoffTimes> {
  const url = `/api/v3/hub-cutoff-times/${menuDate}/${locationId}/`;
  return eatClubClient
    .get(url)
    .then((response) => response.data?.hub_cutoff_times)
    .catch(({response}) => {
      const error = {
        status: response.status,
        message: response.data?.message,
      };
      throw error;
    });
}

export default function useCutoffTimes({
  selectedDate,
  selectedLocation,
  initialCutoffTimes,
}: CutoffTimesProps): CutoffTimesResource {
  const cutoffTimer: MutableRefObject<
    ReturnType<typeof setTimeout> | undefined
  > = useRef();
  const largeOrderCutoffTimer: MutableRefObject<
    ReturnType<typeof setTimeout> | undefined
  > = useRef();

  const [isPastCutoffTime, setIsPastCutoffTime] = useState(false);
  const [isPastLargeOrderCutoffTime, setIsPastLargeOrderCutoffTime] =
    useState(false);

  const {
    data = initialCutoffTimes,
    isFetched,
    isFetching,
  } = useQuery({
    queryKey: ["cutoffTimes", selectedDate, selectedLocation],
    queryFn: () =>
      getCutoffTimes(selectedDate as DateString, selectedLocation as number),
    enabled: Boolean(selectedDate) && Boolean(selectedLocation),
    initialData: initialCutoffTimes,
  });

  const checkCutoffTimer = (
    timerRef: MutableRefObject<NodeJS.Timeout | undefined>,
    timeRemaining: number,
    setterFn: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (timeRemaining > 0) {
      setterFn(false);
      timerRef.current = setTimeout(() => {
        setterFn(true);
      }, timeRemaining);
    } else {
      setterFn(true);
    }
  };

  const timersCleanup = () => {
    if (cutoffTimer.current) {
      clearTimeout(cutoffTimer.current);
    }
    if (largeOrderCutoffTimer.current) {
      clearTimeout(largeOrderCutoffTimer.current);
    }
  };

  useEffect(() => {
    if (data) {
      if (Boolean(selectedDate) && Boolean(selectedLocation)) {
        localStorage.setItem(CUTOFF_TIMES_CACHE_KEY, JSON.stringify(data));
      }
      const timeRemaining = data.order_cutoff_time_remaining * 1000;
      const largeOrderTimeRemaining =
        data.large_group_order_cutoff.time_remaining * 1000;

      checkCutoffTimer(cutoffTimer, timeRemaining, setIsPastCutoffTime);
      checkCutoffTimer(
        largeOrderCutoffTimer,
        largeOrderTimeRemaining,
        setIsPastLargeOrderCutoffTime
      );
    }

    // clear timers on unmount
    return () => {
      timersCleanup();
    };
  }, [data]);

  useEffect(
    () => () => {
      // reset internal state on params changed
      setIsPastCutoffTime(false);
      setIsPastLargeOrderCutoffTime(false);
    },
    [selectedDate, selectedLocation, initialCutoffTimes]
  );

  return {
    cutoffTimes: data,
    isFetched: initialCutoffTimes ? true : isFetched,
    // isFetching checked here to avoid send invalid values based on data related to previos selectedLocation/selectedData
    isPastCutoffTime: isFetching ? false : isPastCutoffTime,
    isPastLargeOrderCutoffTime: isFetching ? false : isPastLargeOrderCutoffTime,
    hasPastCutoffTimer: (data?.order_cutoff_time_remaining ?? 0) > 0,
    hasPastLargeOrderCutoffTimer:
      (data?.large_group_order_cutoff.time_remaining ?? 0) > 0,
  };
}
